import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  HelpAnchor,
  SkeletonCardGrid,
  SurfacePageBody,
  SurfacePageHeader,
  SurfacePageLayout,
} from '@/design-system';
import { Commentable } from '@/shared/comments/Commentable';
import { AskStrip } from '@/versions/v1/chrome-oem/AskStrip';
import { useOemData } from '@/versions/v1/shared/useOemData';
import { MeasureTile, SceneSection } from '@/versions/v1/shared/oem-tiles';
import { LOOPS } from '@/versions/v1/shared/seed/loops';
import { iconForLoop } from './loop-icons';
import { loopSectionStats, loopSectionTiles } from './LoopsSectionScene.transform';

// =========================================================================
// Feedback loops — the section landing. Route `/loops`. Fork-owned (zone C).
//
// Port of the demo's `SECPAGE.loops` (source line 3364): a hero band with the
// section's lead paragraph and four live figures, then a tile per loop.
//
// The demo drew each tile in the loop's own colour over a contour-map SVG.
// The colour is kept — it is carried on the seed and it is how the five loops
// are told apart on the Asset360 home page as well — but the contour
// background is not: at tile size it read as noise, and the section already
// has a hero to carry the mood.
//
// UI layer.
// =========================================================================

export function LoopsSectionScene() {
  // The bulletin count is the one hero figure that is not compiled in.
  const parts = useOemData('parts');
  const stats = useMemo(
    () => loopSectionStats(parts.data ? parts.data.bulletins.length : null),
    [parts.data],
  );
  const tiles = useMemo(() => loopSectionTiles(), []);

  return (
    <SurfacePageLayout width="full">
      <Commentable name="LoopsSectionScene">
        <SurfacePageBody className="@container max-w-[1240px] space-y-8 pt-6">
          <SurfacePageHeader
            kicker="Feedback loops"
            title="What makes the system get smarter"
            description="Five loops take the outcome of every job back into the front of the operating model: components and their cores, warranty and campaigns, rules learned across the fleet, commercial opportunities, and what the demand means for supply."
            actions={<HelpAnchor doc="loops/section" />}
          />

          <AskStrip view="loops" />

          <Commentable name="Stats">
            <div className="grid grid-cols-1 gap-3 @xl:grid-cols-2 @5xl:grid-cols-4">
              {stats.map((stat) => (
                <MeasureTile
                  key={stat.eyebrow}
                  eyebrow={stat.eyebrow}
                  value={stat.value}
                  detail={stat.detail}
                />
              ))}
            </div>
          </Commentable>

          <Commentable name="Tiles">
            <SceneSection
              title="The five loops"
              subtitle="each one feeds the front of the operating model a different way"
            >
              {LOOPS.length === 0 ? (
                <SkeletonCardGrid count={5} cols={2} />
              ) : (
                <ul className="grid list-none grid-cols-1 gap-3 p-0 @2xl:grid-cols-2 @5xl:grid-cols-3">
                  {tiles.map((tile) => {
                    const Icon = iconForLoop(tile.id);
                    return (
                      <li key={tile.id} className="min-w-0">
                        <Link
                          to={tile.to}
                          className="group relative flex h-full min-w-0 flex-col overflow-hidden rounded-xl border p-4 no-underline transition-[background-color,box-shadow,transform] hover:bg-[var(--surface-1)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color-mix(in_srgb,var(--accent)_55%,transparent)] active:translate-y-px"
                          style={{
                            borderColor: 'var(--hairline)',
                            background: 'var(--surface-2)',
                          }}
                        >
                          <span
                            aria-hidden="true"
                            className="absolute inset-y-0 left-0 w-[3px]"
                            style={{ background: tile.color }}
                          />
                          <span className="flex items-start gap-2.5">
                            <span
                              className="flex h-7 w-7 flex-none items-center justify-center rounded-lg"
                              style={{
                                background: `color-mix(in srgb, ${tile.color} 14%, transparent)`,
                                color: tile.color,
                              }}
                            >
                              <Icon className="h-4 w-4" aria-hidden="true" />
                            </span>
                            <span className="min-w-0 flex-1">
                              <span
                                className="block text-[10px] font-semibold uppercase leading-4 tracking-[0.1em]"
                                style={{ color: 'var(--ink-tertiary)' }}
                              >
                                {`Loop ${tile.n} of ${tiles.length} · ${tile.agent} agent`}
                              </span>
                              <span
                                className="mt-0.5 block text-[14px] font-semibold leading-snug"
                                style={{ color: 'var(--ink-primary)' }}
                              >
                                {tile.name}
                              </span>
                            </span>
                            <span
                              className="mono flex-none text-[13px] font-semibold"
                              style={{ color: 'var(--ink-tertiary)' }}
                            >
                              {tile.items}
                            </span>
                          </span>
                          <span
                            className="mt-2.5 block text-[12px] leading-[1.55]"
                            style={{ color: 'var(--ink-secondary)' }}
                          >
                            {tile.blurb}
                          </span>
                          <span
                            className="mt-3 block text-[11.5px] font-medium"
                            style={{ color: 'var(--accent)' }}
                          >
                            {`Open · ${tile.items} things this loop does ›`}
                          </span>
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              )}
            </SceneSection>
          </Commentable>
        </SurfacePageBody>
      </Commentable>
    </SurfacePageLayout>
  );
}

export default LoopsSectionScene;
